import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  Calendar,
  Clock,
  UserX,
  AlertTriangle,
  Camera,
  Edit3,
  Download,
  Loader2,
  Users,
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, subDays } from 'date-fns';
import { reportsApi, employeesApi } from '../services/api';

const Reports = () => {
  const { user, locations, selectedLocation } = useAuth();
  const [activeTab, setActiveTab] = useState('employee');
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Filters
  const [preset, setPreset] = useState('this-week');
  const [startDate, setStartDate] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [locationFilter, setLocationFilter] = useState(selectedLocation || 'all');
  const [employeeFilter, setEmployeeFilter] = useState('all');

  // Data
  const [employeeReport, setEmployeeReport] = useState(null);
  const [locationReport, setLocationReport] = useState(null);

  const canAccess = ['admin', 'accounting_admin', 'manager', 'assistant_manager'].includes(user?.role);

  useEffect(() => {
    if (!canAccess) return;
    employeesApi.getAll().then(setEmployees).catch(() => setEmployees([]));
  }, [canAccess]);

  const applyPreset = (p) => {
    setPreset(p);
    const today = new Date();
    if (p === 'today') {
      setStartDate(format(today, 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    } else if (p === 'this-week') {
      setStartDate(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
      setEndDate(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
    } else if (p === 'last-7') {
      setStartDate(format(subDays(today, 6), 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    } else if (p === 'last-14') {
      setStartDate(format(subDays(today, 13), 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    } else if (p === 'last-30') {
      setStartDate(format(subDays(today, 29), 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    }
  };

  const runReport = async () => {
    setLoading(true);
    try {
      if (activeTab === 'employee') {
        const data = await reportsApi.employee({
          startDate, endDate,
          locationId: locationFilter,
          employeeId: employeeFilter,
        });
        setEmployeeReport(data);
      } else {
        const data = await reportsApi.location({
          startDate, endDate,
          locationId: locationFilter,
        });
        setLocationReport(data);
      }
    } catch (err) {
      alert('Failed to run report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    let rows;
    let sheetName;
    if (activeTab === 'employee' && employeeReport) {
      rows = employeeReport.rows.map((r) => ({
        Employee: r.name,
        'Employee #': r.employee_number || '',
        Location: r.location_id || '',
        Position: r.position || '',
        Scheduled: r.scheduled_shifts,
        Completed: r.completed_shifts,
        'Late In': r.late_clock_ins,
        'No-Shows': r.no_shows,
        'Missed Clock-Out': r.missed_clock_outs,
        'Early Out': r.early_clock_outs,
        'Outside Schedule': r.worked_outside_schedule,
        'Sched Hrs': r.scheduled_hours,
        'Actual Hrs': r.actual_hours,
        'Reg Hrs': r.regular_hours,
        'OT Hrs': r.overtime_hours,
        'No Photo (in)': r.photo_missing_in,
        'No Photo (out)': r.photo_missing_out,
        'Manual In': r.manual_edited_in,
        'Manual Out': r.manual_edited_out,
      }));
      sheetName = 'Employee Report';
    } else if (locationReport) {
      const s = locationReport.stats;
      rows = [{
        Locations: locationFilter,
        'Employee Count': locationReport.employee_count,
        Scheduled: s.scheduled_shifts,
        Completed: s.completed_shifts,
        'Late In': s.late_clock_ins,
        'No-Shows': s.no_shows,
        'Missed Clock-Out': s.missed_clock_outs,
        'Early Out': s.early_clock_outs,
        'Sched Hrs': s.scheduled_hours,
        'Actual Hrs': s.actual_hours,
        'Reg Hrs': s.regular_hours,
        'OT Hrs': s.overtime_hours,
        'No Photo (in)': s.photo_missing_in,
        'No Photo (out)': s.photo_missing_out,
        'Manual In': s.manual_edited_in,
        'Manual Out': s.manual_edited_out,
      }];
      sheetName = 'Location Report';
    } else {
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
    XLSX.writeFile(wb, `${sheetName.replace(' ', '_')}_${startDate}_to_${endDate}.xlsx`);
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-gray-500">
        Reports are available to Managers, Assistant Managers, and Admins only.
      </div>
    );
  }

  const renderStatTile = (label, value, Icon, color = 'text-purple-600', bg = 'bg-purple-50') => (
    <div className={`p-4 rounded-lg ${bg} border border-gray-200`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-purple-600" />
            Employee Report
          </h1>
          <p className="text-gray-500 mt-1">Attendance, schedules, and flags by employee or location.</p>
        </div>
        <Button onClick={exportExcel} variant="outline" disabled={!(employeeReport || locationReport)} data-testid="export-report-btn">
          <Download className="w-4 h-4 mr-2" />Export Excel
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="employee" data-testid="employee-report-tab"><Users className="w-4 h-4 mr-2" />By Employee</TabsTrigger>
          <TabsTrigger value="location" data-testid="location-report-tab"><Calendar className="w-4 h-4 mr-2" />Location Summary</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="border-0 shadow-md mb-6">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs">Date Range</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger data-testid="report-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="last-7">Last 7 Days</SelectItem>
                <SelectItem value="last-14">Last 14 Days</SelectItem>
                <SelectItem value="last-30">Last 30 Days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset('custom'); }} data-testid="report-start-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset('custom'); }} data-testid="report-end-date" />
          </div>
          <div>
            <Label className="text-xs">Location</Label>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger data-testid="report-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {(locations || []).map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {activeTab === 'employee' && (
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger data-testid="report-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_number})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={runReport} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="run-report-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
            Run Report
          </Button>
        </CardContent>
      </Card>

      {/* Employee Report */}
      {activeTab === 'employee' && employeeReport && (
        <Card className="border-0 shadow-md" data-testid="employee-report-table">
          <CardHeader>
            <CardTitle className="text-base">Employee Stats ({employeeReport.rows.length} rows)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 text-xs">
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Sched</TableHead>
                  <TableHead className="text-right">Done</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                  <TableHead className="text-right">No-Show</TableHead>
                  <TableHead className="text-right">Missed Out</TableHead>
                  <TableHead className="text-right">Early Out</TableHead>
                  <TableHead className="text-right">Outside</TableHead>
                  <TableHead className="text-right">Sched Hrs</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                  <TableHead className="text-right">No Photo</TableHead>
                  <TableHead className="text-right">Manual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeReport.rows.map((r) => (
                  <TableRow key={r.employee_id} className="hover:bg-gray-50" data-testid={`report-row-${r.employee_id}`}>
                    <TableCell className="text-sm">
                      <div className="font-medium text-gray-900">{r.name}</div>
                      <div className="text-xs text-gray-400">{r.employee_number || '—'}</div>
                    </TableCell>
                    <TableCell className="text-right text-sm">{r.scheduled_shifts}</TableCell>
                    <TableCell className="text-right text-sm">{r.completed_shifts}</TableCell>
                    <TableCell className="text-right text-sm">{r.late_clock_ins > 0 ? <Badge className="bg-amber-100 text-amber-700">{r.late_clock_ins}</Badge> : '0'}</TableCell>
                    <TableCell className="text-right text-sm">{r.no_shows > 0 ? <Badge className="bg-red-100 text-red-700">{r.no_shows}</Badge> : '0'}</TableCell>
                    <TableCell className="text-right text-sm">{r.missed_clock_outs > 0 ? <Badge className="bg-orange-100 text-orange-700">{r.missed_clock_outs}</Badge> : '0'}</TableCell>
                    <TableCell className="text-right text-sm">{r.early_clock_outs}</TableCell>
                    <TableCell className="text-right text-sm">{r.worked_outside_schedule}</TableCell>
                    <TableCell className="text-right text-sm">{r.scheduled_hours.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{r.actual_hours.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm">{r.overtime_hours > 0 ? <span className="text-amber-600 font-medium">{r.overtime_hours.toFixed(2)}</span> : '0.00'}</TableCell>
                    <TableCell className="text-right text-sm">{r.photo_missing_in + r.photo_missing_out}</TableCell>
                    <TableCell className="text-right text-sm">{r.manual_edited_in + r.manual_edited_out}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {employeeReport.rows.length === 0 && (
              <div className="text-center text-gray-400 py-8">No employees in this date range</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Location Report */}
      {activeTab === 'location' && locationReport && (
        <div className="space-y-6" data-testid="location-report-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {renderStatTile('Employees', locationReport.employee_count, Users)}
            {renderStatTile('Scheduled', locationReport.stats.scheduled_shifts, Calendar)}
            {renderStatTile('Completed', locationReport.stats.completed_shifts, Clock, 'text-green-600', 'bg-green-50')}
            {renderStatTile('Late', locationReport.stats.late_clock_ins, AlertTriangle, 'text-amber-600', 'bg-amber-50')}
            {renderStatTile('No-Shows', locationReport.stats.no_shows, UserX, 'text-red-600', 'bg-red-50')}
            {renderStatTile('Missed Clock-Out', locationReport.stats.missed_clock_outs, Clock, 'text-orange-600', 'bg-orange-50')}
            {renderStatTile('Early Outs', locationReport.stats.early_clock_outs, Clock, 'text-orange-600', 'bg-orange-50')}
            {renderStatTile('Outside Schedule', locationReport.stats.worked_outside_schedule, AlertTriangle, 'text-amber-600', 'bg-amber-50')}
            {renderStatTile('Scheduled Hrs', locationReport.stats.scheduled_hours.toFixed(1), Clock)}
            {renderStatTile('Actual Hrs', locationReport.stats.actual_hours.toFixed(1), Clock, 'text-green-600', 'bg-green-50')}
            {renderStatTile('Overtime Hrs', locationReport.stats.overtime_hours.toFixed(1), Clock, 'text-amber-600', 'bg-amber-50')}
            {renderStatTile('Missing Photos', locationReport.stats.photo_missing_in + locationReport.stats.photo_missing_out, Camera)}
            {renderStatTile('Manual Edits', locationReport.stats.manual_edited_in + locationReport.stats.manual_edited_out, Edit3)}
          </div>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-base">Employees with Attendance Issues</CardTitle>
            </CardHeader>
            <CardContent>
              {locationReport.problem_employees.length === 0 ? (
                <p className="text-gray-400 text-sm">No issues in this date range.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 text-xs">
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Late</TableHead>
                      <TableHead className="text-right">No-Show</TableHead>
                      <TableHead className="text-right">Missed Out</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locationReport.problem_employees.map((e) => (
                      <TableRow key={e.employee_id}>
                        <TableCell className="font-medium">{e.name}</TableCell>
                        <TableCell className="text-right">{e.late_clock_ins}</TableCell>
                        <TableCell className="text-right">{e.no_shows}</TableCell>
                        <TableCell className="text-right">{e.missed_clock_outs}</TableCell>
                        <TableCell className="text-right font-medium"><Badge className="bg-red-100 text-red-700">{e.total_issues}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!employeeReport && !locationReport && !loading && (
        <Card className="border-0 shadow-md">
          <CardContent className="text-center py-16 text-gray-400">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            Set your filters above and click <strong>Run Report</strong>.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
